import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Stack,
  Button,
  Container,
  Typography,
  Grid,
  Divider,
  Skeleton,
  IconButton,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Page from '../../components/Page';
import MoneyInput, { IntegerInput, toFiniteInteger, toFiniteNumber } from '../../components/MoneyInput';
import tracker from '../../api/tracker';
import useNotification from '../../hooks/useNotificaction';

export default function PricingSettings() {
  const { t } = useTranslation();
  const [setConf] = useNotification();

  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [config, setConfig]     = useState(null);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await tracker.get('admin/pricing');
      setConfig(res.data);
    } catch (error) {
      setConf({ msg: t('error_loading') || 'Error al cargar la configuración', variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (path, value) => {
    setConfig((prev) => {
      const next = { ...prev };
      let cursor = next;
      for (let i = 0; i < path.length - 1; i += 1) {
        cursor[path[i]] = { ...cursor[path[i]] };
        cursor = cursor[path[i]];
      }
      cursor[path[path.length - 1]] = value;
      return next;
    });
  };

  const handleSiblingTierChange = (count, amount) => {
    setConfig((prev) => ({
      ...prev,
      sibling_tiers: { ...prev.sibling_tiers, [count]: amount },
    }));
  };

  const handleAddSiblingTier = () => {
    setConfig((prev) => {
      const keys = Object.keys(prev.sibling_tiers).map(Number);
      const nextKey = keys.length ? Math.max(...keys) + 1 : 1;
      return { ...prev, sibling_tiers: { ...prev.sibling_tiers, [nextKey]: 0 } };
    });
  };

  const handleRemoveSiblingTier = (count) => {
    setConfig((prev) => {
      const tiers = { ...prev.sibling_tiers };
      delete tiers[count];
      return { ...prev, sibling_tiers: tiers };
    });
  };

  const normalizeConfig = (raw) => ({
    ...raw,
    sacrament_fees: Object.fromEntries(
      Object.entries(raw.sacrament_fees || {}).map(([key, amount]) => [key, toFiniteNumber(amount)])
    ),
    religious_education: {
      ...raw.religious_education,
      baptism_exempt_max_age: toFiniteInteger(raw.religious_education?.baptism_exempt_max_age),
      child_max_age: toFiniteInteger(raw.religious_education?.child_max_age),
      child_amount: toFiniteNumber(raw.religious_education?.child_amount),
      adult_amount: toFiniteNumber(raw.religious_education?.adult_amount),
    },
    sibling_tiers: Object.fromEntries(
      Object.entries(raw.sibling_tiers || {}).map(([count, amount]) => [count, toFiniteNumber(amount)])
    ),
    sibling_extra_amount: toFiniteNumber(raw.sibling_extra_amount),
    document_request: {
      ...(raw.document_request || {}),
      amount: toFiniteNumber(raw.document_request?.amount ?? 11),
    },
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = normalizeConfig(config);
      const res = await tracker.post('admin/pricing', payload);
      setConfig(res.data);
      setConf({ msg: t('saved_successfully') || 'Donaciones actualizadas correctamente', variant: 'success' });
    } catch (error) {
      setConf({ msg: t('error_saving') || 'Error al guardar la configuración', variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page title={t('pricing_settings') || 'Donaciones de Sacramentos'}>
      <Container maxWidth="md">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            {t('pricing_settings') || 'Donaciones de Sacramentos'}
          </Typography>
        </Stack>

        {loading || !config ? (
          <Stack spacing={2}>
            <Skeleton variant="rectangular" height={120} />
            <Skeleton variant="rectangular" height={160} />
            <Skeleton variant="rectangular" height={160} />
          </Stack>
        ) : (
          <Stack spacing={3}>
            {/* Sacrament donation (used when RE does not apply, e.g. infant baptism) */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('sacrament_fees') || 'Donaciones por sacramento'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('sacrament_fee_when_no_re_hint')}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={2}>
                  {Object.entries(config.sacrament_fees).map(([key, value]) => (
                    <Grid item xs={12} sm={6} md={3} key={key}>
                      <MoneyInput
                        fullWidth
                        label={t(key) || key}
                        value={value}
                        onChange={(next) => setField(['sacrament_fees', key], next)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            {/* Religious Education */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('religious_education') || 'Educación Religiosa'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('religious_education_only_hint')}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <IntegerInput
                      fullWidth
                      label={t('baptism_exempt_max_age') || 'Edad máxima exenta (bautismo)'}
                      value={config.religious_education.baptism_exempt_max_age}
                      onChange={(next) => setField(['religious_education', 'baptism_exempt_max_age'], next)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <IntegerInput
                      fullWidth
                      label={t('child_max_age') || 'Edad máxima para tarifa de niño'}
                      value={config.religious_education.child_max_age}
                      onChange={(next) => setField(['religious_education', 'child_max_age'], next)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MoneyInput
                      fullWidth
                      label={t('child_amount') || 'Donación de niño'}
                      value={config.religious_education.child_amount}
                      onChange={(next) => setField(['religious_education', 'child_amount'], next)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MoneyInput
                      fullWidth
                      label={t('adult_amount') || 'Donación de adulto'}
                      value={config.religious_education.adult_amount}
                      onChange={(next) => setField(['religious_education', 'adult_amount'], next)}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('document_request_donation') || 'Document request donation'}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <MoneyInput
                      fullWidth
                      label={t('document_request_amount') || 'Amount'}
                      value={config.document_request?.amount ?? 11}
                      onChange={(next) => setField(['document_request', 'amount'], next)}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Sibling Discounts */}
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6">
                    {t('sibling_discounts') || 'Descuentos por Hermanos'}
                  </Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={handleAddSiblingTier}>
                    {t('add_tier') || 'Agregar tramo'}
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {t('sibling_discounts_hint') || 'Monto total de educación religiosa según el número de hermanos registrados juntos.'}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                  {Object.entries(config.sibling_tiers)
                    .sort((a, b) => Number(a[0]) - Number(b[0]))
                    .map(([count, amount]) => (
                      <Grid item xs={12} sm={6} md={4} key={count}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <MoneyInput
                            fullWidth
                            label={`${count} ${Number(count) === 1 ? (t('sibling') || 'hermano') : (t('siblings') || 'hermanos')}`}
                            value={amount}
                            onChange={(next) => handleSiblingTierChange(count, next)}
                          />
                          <IconButton color="error" onClick={() => handleRemoveSiblingTier(count)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Grid>
                    ))}
                </Grid>
                <Divider sx={{ my: 2 }} />
                <MoneyInput
                  label={t('sibling_extra_amount') || 'Monto adicional por hermano extra'}
                  value={config.sibling_extra_amount}
                  onChange={(next) => setField(['sibling_extra_amount'], next)}
                  sx={{ maxWidth: 320 }}
                />
              </CardContent>
            </Card>

            <Stack direction="row" justifyContent="flex-end">
              <LoadingButton
                variant="contained"
                size="large"
                loading={saving}
                onClick={handleSave}
                sx={{ background: '#032C65', '&:hover': { background: '#021a40' } }}
              >
                {t('save') || 'Guardar'}
              </LoadingButton>
            </Stack>
          </Stack>
        )}
      </Container>
    </Page>
  );
}
